/**
 * Normalizes user-supplied images into something the KYC endpoint accepts.
 *
 * The submit-kyc endpoint only allows .jpg/.jpeg/.png and caps each file at
 * 10 MB, and it does no decoding of its own, so whatever we send has to be a
 * real, browser-renderable JPEG - the admin review screen displays these with
 * a plain <img> tag. Two common cases fail that contract:
 *
 *  - HEIC/HEIF, which is what macOS and iOS hand over for camera photos.
 *  - Oversized JPEGs from high-resolution phone cameras.
 *
 * Both are fixed here, in the browser, before the file reaches the API.
 */

const JPEG_TYPE = 'image/jpeg'

// Comfortably below the endpoint's 10 MB per-file cap.
const MAX_BYTES = 8 * 1024 * 1024

// The first encode keeps a phone photo at its native resolution - a 12 MP
// shot is 4032x3024, and a reviewer has to read the last four digits of a
// card off it. It is capped rather than unbounded because Safari refuses to
// rasterize a canvas much beyond ~16 M pixels.
const FULL_MAX_EDGE = 4032

// Used only when the full-resolution encode is still over the size cap.
const REDUCED_MAX_EDGE = 2400

// Tried in order until the encoded result fits under MAX_BYTES.
const QUALITY_STEPS = [0.92, 0.85, 0.75, 0.65, 0.55]

const PASSTHROUGH_TYPES = [JPEG_TYPE, 'image/png']

const UNREADABLE_MESSAGE = 'We could not read that picture. Please try a different photo.'

export function getExtension(name) {
  const match = /\.([a-z0-9]+)$/i.exec(name || '')
  return match ? match[1].toLowerCase() : ''
}

export function isHeic(file) {
  if (!file) {
    return false
  }
  const extension = getExtension(file.name)
  return (
    extension === 'heic' ||
    extension === 'heif' ||
    /^image\/hei[cf]/i.test(file.type || '')
  )
}

/**
 * Files that already satisfy the endpoint are left untouched, so the common
 * case (a normal JPEG under the cap) keeps its original quality and metadata.
 */
export function needsTranscode(file) {
  if (!file) {
    return false
  }
  if (isHeic(file)) {
    return true
  }
  if (file.size > MAX_BYTES) {
    return true
  }
  const extension = getExtension(file.name)
  const extensionAllowed = ['jpg', 'jpeg', 'png'].includes(extension)
  return !extensionAllowed || !PASSTHROUGH_TYPES.includes(file.type)
}

function toJpegName(name) {
  const base = (name || 'upload').replace(/\.[a-z0-9]+$/i, '')
  return `${base || 'upload'}.jpg`
}

function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const image = new Image()

    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error(UNREADABLE_MESSAGE))
    }

    image.src = url
  })
}

/**
 * libheif is a megabyte of WASM, so it is imported lazily and only reached
 * when the browser itself cannot decode the file. Its failures are reported
 * in its own vocabulary, which is no use to someone uploading an ID, so they
 * are replaced with something actionable.
 */
async function decodeWithLibheif(file) {
  try {
    const heic2any = (await import(/* webpackChunkName: "heic2any" */ 'heic2any')).default
    const converted = await heic2any({ blob: file, toType: JPEG_TYPE, quality: QUALITY_STEPS[0] })

    return await loadImage(Array.isArray(converted) ? converted[0] : converted)
  } catch (error) {
    throw new Error(UNREADABLE_MESSAGE)
  }
}

// Safari decodes HEIC natively, so try the browser before paying for libheif.
async function decode(file) {
  if (!isHeic(file)) {
    return loadImage(file)
  }

  try {
    return await loadImage(file)
  } catch (error) {
    return decodeWithLibheif(file)
  }
}

function encode(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error(UNREADABLE_MESSAGE))),
      JPEG_TYPE,
      quality
    )
  })
}

function drawScaled(image, maxEdge) {
  const width = image.naturalWidth || image.width
  const height = image.naturalHeight || image.height

  if (!width || !height) {
    throw new Error(UNREADABLE_MESSAGE)
  }

  const scale = Math.min(1, maxEdge / Math.max(width, height))
  const canvas = document.createElement('canvas')

  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)

  const context = canvas.getContext('2d')

  // Null comes back when the browser cannot allocate the context at all,
  // which a full-resolution canvas on a phone can genuinely provoke.
  if (!context) {
    throw new Error(UNREADABLE_MESSAGE)
  }

  // JPEG has no alpha channel, so anything transparent in a PNG would encode
  // as black without a background painted underneath it first.
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(image, 0, 0, canvas.width, canvas.height)

  return canvas
}

/**
 * @param {File} file
 * @returns {Promise<File>} the original file when it already qualifies,
 *   otherwise a re-encoded JPEG under the endpoint's size cap.
 */
export async function normalizeImageFile(file) {
  if (!needsTranscode(file)) {
    return file
  }

  const image = await decode(file)

  // Full resolution first - most phone photos fit under the cap once encoded
  // as JPEG, and downscaling them would throw away detail for nothing.
  let encoded = await encode(drawScaled(image, FULL_MAX_EDGE), QUALITY_STEPS[0])

  if (encoded.size > MAX_BYTES) {
    const canvas = drawScaled(image, REDUCED_MAX_EDGE)

    for (const quality of QUALITY_STEPS) {
      encoded = await encode(canvas, quality)

      if (encoded.size <= MAX_BYTES) {
        break
      }
    }
  }

  if (encoded.size > MAX_BYTES) {
    throw new Error('The image is too large to upload. Please use a smaller picture.')
  }

  return new File([encoded], toJpegName(file.name), {
    type: JPEG_TYPE,
    lastModified: Date.now()
  })
}
